import React, { useEffect, useState } from 'react';
import { ChevronIcon, CollapseIcon, ExpandIcon } from '../../../../bombiix-icon';

/** Card-header glyph; the icon-container primitive is a 20px box with a 16px glyph. */
const ICON_SIZE_CARD = 16;

/**
 * Deliberate exception to the size={32} floating-section convention. The
 * `.landed-floting-toggle` tab is a fixed 20x20px box that sits flush with the
 * viewport edge when collapsed (`top: calc(100% - 20px)`), so a larger glyph
 * would overflow the tab and break that alignment.
 */
const ICON_SIZE_FLOATING_TOGGLE = 20;

/** Controls handed to `children` so a consumer rendering its own header can drive the card. */
export interface CostingCardControls {
  isFull: boolean;
  toggleFull: () => void;
  title: string;
}

export interface CostingCardShellProps {
  title: string;
  /**
   * `fullpage` turns the card into a fixed full-page overlay and locks body scroll.
   * `column` only tags the card, leaving layout to the parent, and does not lock scroll.
   */
  expandMode?: 'fullpage' | 'column';
  /** Fires after every expand/collapse toggle. */
  onFullChange?: ((isFull: boolean) => void) | undefined;
  /** Wraps the card in `.landed-floting-container` so it renders as a viewport-pinned panel. */
  floating?: boolean;
  /** Skips the default title/expand header and hands those controls to `children` instead. */
  renderOwnHeader?: boolean;
  children: (fullView: boolean, controls: CostingCardControls) => React.ReactNode;
}

const CARD_COLLAPSE_DURATION_MS = 240;

/**
 * Costing card wrapper: white title header with a fullscreen-expand icon.
 *
 * `children` is a render function `(fullView, controls) => node` so the same grid instance is
 * mounted in both the normal and expanded states — editable state stays bound to a single context
 * rather than being torn down and rebuilt on every toggle.
 */
function CostingCardShell({ title, expandMode = 'fullpage', onFullChange, floating = false, renderOwnHeader = false, children }: CostingCardShellProps): React.JSX.Element {
  const [isFull, setIsFull] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isFloatingCollapsed, setIsFloatingCollapsed] = useState(false);

  useEffect(() => {
    if (onFullChange) onFullChange(isFull);
  }, [isFull, onFullChange]);

  useEffect(() => {
    if (!isFull || expandMode !== 'fullpage') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFull, expandMode]);

  useEffect(() => {
    if (!isClosing) return undefined;
    const timer = setTimeout(() => {
      setIsClosing(false);
      setIsFull(false);
    }, CARD_COLLAPSE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isClosing]);

  const expandedClass = expandMode === 'column' ? 'expand-card-view-column' : 'expand-card-view-container';
  const toggleFull = () => {
    if (isClosing) return;
    if (isFull) {
      setIsClosing(true);
      return;
    }
    setIsFull(true);
  };

  const card = (
    <div className={`costing-card${isFull ? ` ${expandedClass}` : ''}${isClosing ? ' costing-card--closing' : ''}`}>
      {!renderOwnHeader && (
        <div className="costing-card-header">
          <p className="product-cp-title mb-0">{title}</p>
          <button
            type="button"
            /* `--solid-dark`, not `--dark`: the `--dark` modifier is a HOVER variant, so the button
               read as an unstyled glyph until the pointer arrived. This toggle sits on the card's
               white chrome and has to look like a button at rest, so it is filled by default and
               shifts to the standard Bombiix Blue on hover, matching every other icon button. */
            className="bombiix-icon-container bombiix-icon-container--solid-dark"
            aria-label={isFull ? 'Collapse' : 'Expand'}
            title={isFull ? 'Collapse' : 'Expand'}
            onClick={toggleFull}
          >
            {isFull ? <CollapseIcon size={ICON_SIZE_CARD} /> : <ExpandIcon size={ICON_SIZE_CARD} />}
          </button>
        </div>
      )}
      <div className={`costing-card-body${renderOwnHeader ? ' costing-card-body--header' : ''}`}>{children(isFull, { isFull, toggleFull, title })}</div>
    </div>
  );

  if (!floating) return card;
  return (
    <>
      {!isFull && (
        <button
          type="button"
          className={`landed-floting-toggle${isFloatingCollapsed ? ' landed-floting-toggle--collapsed' : ''}`}
          aria-label={isFloatingCollapsed ? 'Show landed information' : 'Hide landed information'}
          aria-expanded={!isFloatingCollapsed}
          title={isFloatingCollapsed ? 'Show' : 'Hide'}
          onClick={() => setIsFloatingCollapsed((prev) => !prev)}
        >
          <ChevronIcon size={ICON_SIZE_FLOATING_TOGGLE} />
        </button>
      )}
      <div className={`landed-floting-container${isFloatingCollapsed ? ' landed-floting-container--collapsed' : ''}`}>{card}</div>
    </>
  );
}

export default CostingCardShell;
